import React, { useState } from 'react';
import { 
  Badge, 
  Dropdown, 
  Button, 
  List, 
  Typography, 
  Empty, 
  Spin,
  Space,
  Divider,
  Tag,
  Tooltip
} from 'antd';
import { 
  BellOutlined, 
  CheckOutlined, 
  DeleteOutlined,
  EyeOutlined,
  CalendarOutlined,
  NotificationOutlined,
  SettingOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { useNotificaciones } from './NotificacionesContext';
import moment from 'moment';
import 'moment/locale/es';

const { Text } = Typography;

// Configurar moment en español
moment.locale('es');

const NotificationBell: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const { 
    notificaciones, 
    conteo, 
    loading, 
    marcarComoLeida, 
    eliminar, 
    marcarTodasComoLeidas,
    refrescar 
  } = useNotificaciones();

  const handleMarcarComoLeida = async (notificacionId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    await marcarComoLeida(notificacionId);
  };

  const handleEliminar = async (notificacionId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    await eliminar(notificacionId);
  };

  const handleMarcarTodasComoLeidas = async () => {
    await marcarTodasComoLeidas();
  };

  const handleRefrescar = async () => {
    await refrescar();
  };

  const getIconoCategoria = (categoria: string) => {
    switch (categoria) {
      case 'agenda':
        return <CalendarOutlined style={{ color: '#1890ff' }} />;
      case 'sistema':
        return <SettingOutlined style={{ color: '#722ed1' }} />;
      case 'recordatorio':
        return <NotificationOutlined style={{ color: '#fa8c16' }} />;
      default:
        return <InfoCircleOutlined style={{ color: '#52c41a' }} />;
    }
  };

  const getColorPrioridad = (prioridad: string) => {
    switch (prioridad) {
      case 'alta':
        return '#ff4d4f';
      case 'baja':
        return '#1890ff';
      default:
        return '#52c41a';
    }
  };

  const getTagCategoria = (categoria: string) => {
    const configs = {
      general: { color: 'default', text: 'General' },
      agenda: { color: 'blue', text: 'Agenda' },
      sistema: { color: 'purple', text: 'Sistema' },
      recordatorio: { color: 'orange', text: 'Recordatorio' }
    };
    
    const config = configs[categoria as keyof typeof configs] || configs.general;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const renderNotificacion = (notificacion: any) => (
    <List.Item
      key={notificacion.id}
      style={{
        padding: '12px 16px',
        borderLeft: `3px solid ${getColorPrioridad(notificacion.prioridad)}`,
        backgroundColor: notificacion.leida ? '#fafafa' : '#ffffff',
        borderBottom: '1px solid #f0f0f0'
      }}
      actions={[
        !notificacion.leida && (
          <Tooltip title="Marcar como leída">
            <Button 
              type="text" 
              size="small" 
              icon={<EyeOutlined />}
              onClick={(e) => handleMarcarComoLeida(notificacion.id, e)}
            />
          </Tooltip>
        ),
        <Tooltip title="Eliminar">
          <Button 
            type="text" 
            size="small" 
            danger
            icon={<DeleteOutlined />}
            onClick={(e) => handleEliminar(notificacion.id, e)}
          />
        </Tooltip>
      ].filter(Boolean)}
    >
      <List.Item.Meta
        avatar={getIconoCategoria(notificacion.categoria)}
        title={
          <Space size={8}>
            <Text strong={!notificacion.leida} style={{ fontSize: '14px' }}>
              {notificacion.titulo}
            </Text>
            {getTagCategoria(notificacion.categoria)}
          </Space>
        }
        description={
          <div>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>
              {notificacion.descripcion}
            </Text>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {moment(notificacion.fechaCreacion).fromNow()}
            </Text>
          </div>
        }
      />
    </List.Item>
  );

  const dropdownContent = (
    <div style={{ width: 350, maxHeight: 400, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Text strong>Notificaciones</Text>
          <Space>
            <Button 
              type="text" 
              size="small"
              onClick={handleRefrescar}
              loading={loading}
            >
              Actualizar
            </Button>
            {conteo && conteo.noLeidas > 0 && (
              <Button 
                type="text" 
                size="small"
                onClick={handleMarcarTodasComoLeidas}
                icon={<CheckOutlined />}
              >
                Marcar todas
              </Button>
            )}
          </Space>
        </Space>
      </div>
      
      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin />
          </div>
        ) : notificaciones.length === 0 ? (
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No hay notificaciones"
            style={{ padding: '20px' }}
          />
        ) : (
          <List
            dataSource={notificaciones.slice(0, 10)} // Mostrar solo las primeras 10
            renderItem={renderNotificacion}
            split={false}
            style={{ padding: 0 }}
          />
        )}
      </div>
      
      {notificaciones.length > 10 && (
        <>
          <Divider style={{ margin: 0 }} />
          <div style={{ padding: '8px 16px', textAlign: 'center' }}>
            <Button type="link" size="small">
              Ver todas las notificaciones
            </Button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <Dropdown
      overlay={dropdownContent}
      trigger={['click']}
      placement="bottomRight"
      open={visible}
      onOpenChange={setVisible}
    >
      <Badge 
        count={conteo?.noLeidas || 0} 
        size="small"
        offset={[-3, 3]}
      >
        <Button 
          type="text" 
          icon={<BellOutlined style={{ fontSize: '18px' }} />}
          style={{ 
            border: 'none',
            color: conteo && conteo.noLeidas > 0 ? '#1890ff' : '#595959'
          }}
        />
      </Badge>
    </Dropdown>
  );
};

export default NotificationBell;
